import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Shield, ChevronLeft, ChevronRight, Search, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { api } from '../../lib/api';
import { queryKeys } from '../../lib/queryClient';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const ACTION_CATEGORY_STYLES = {
  LOGIN_SUCCESS:        'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  LOGIN_FAILED:         'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  LOGOUT:               'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  PASSWORD_CHANGE:      'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  STUDENT_CREATE:       'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  STUDENT_VIEW:         'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
  STUDENT_UPDATE:       'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  STUDENT_DELETE:       'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  STUDENT_EXPORT:       'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  SECURITY_VIOLATION:   'bg-red-200 text-red-800 dark:bg-red-900/50 dark:text-red-200 font-semibold',
  RATE_LIMIT_HIT:       'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  SUSPICIOUS_ACTIVITY:  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  IMPERSONATION_START:  'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  IMPERSONATION_END:    'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  BULK_OPERATION:       'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  DATA_EXPORT:          'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  DATA_IMPORT:          'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  SENSITIVE_DATA_ACCESS:'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  PERMISSION_GRANT:     'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  PERMISSION_REVOKE:    'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  ROLE_CHANGE:          'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
};

const PAGE_SIZE = 50;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatTimestamp(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleString('en-IE', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Small components
// ---------------------------------------------------------------------------
function ActionBadge({ action, display }) {
  const style = ACTION_CATEGORY_STYLES[action] || 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400';
  return (
    <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${style}`}>
      {display || action}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Student filter combobox
// ---------------------------------------------------------------------------
function StudentFilter({ onSelect, onClear, selectedName }) {
  const { t } = useTranslation();
  const [query, setQuery]       = useState('');
  const [results, setResults]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [open, setOpen]         = useState(false);
  const debounceRef             = useRef(null);
  const containerRef            = useRef(null);

  useEffect(() => {
    if (!query.trim()) { setResults([]); setOpen(false); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await api.students.getAll({ search: query, page_size: 8 });
        setResults(data.results || []);
        setOpen(true);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleSelect(student) {
    setQuery('');
    setResults([]);
    setOpen(false);
    onSelect(student);
  }

  function handleClear() {
    setQuery('');
    setResults([]);
    setOpen(false);
    onClear();
  }

  if (selectedName) {
    return (
      <div className="flex items-center gap-1.5 h-8 px-3 rounded-md border border-blue-300 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700 text-blue-700 dark:text-blue-300 text-xs font-medium">
        <span>{selectedName}</span>
        <button onClick={handleClear} className="hover:text-blue-900 dark:hover:text-blue-100 ml-0.5">
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
        <input
          className="h-8 pl-8 pr-3 text-xs rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring w-48"
          placeholder={t('auditLog.filter.studentPlaceholder')}
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        {loading && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 animate-spin" />}
      </div>
      {open && results.length > 0 && (
        <div className="absolute top-full mt-1 left-0 w-72 bg-card border rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
          {results.map(student => (
            <button
              key={student.uuid}
              onClick={() => handleSelect(student)}
              className="w-full text-left px-3 py-2.5 hover:bg-muted/70 transition-colors"
            >
              <div className="text-sm font-medium">{student.full_name}</div>
              <div className="text-xs text-gray-400">{student.student_id}{student.course_name ? ` · ${student.course_name}` : ''}</div>
            </button>
          ))}
        </div>
      )}
      {open && !loading && query.trim() && results.length === 0 && (
        <div className="absolute top-full mt-1 left-0 w-64 bg-card border rounded-lg shadow-lg z-50 px-3 py-4 text-xs text-gray-500 dark:text-gray-400 text-center">
          {t('auditLog.filter.noStudents')}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function AuditLogPage() {
  const { t } = useTranslation();

  const [userEmail, setUserEmail]     = useState('');
  const [dateFrom, setDateFrom]       = useState('');
  const [dateTo, setDateTo]           = useState('');
  const [studentUuid, setStudentUuid] = useState('');
  const [studentName, setStudentName] = useState('');
  const [page, setPage]               = useState(1);

  const resetPage = (setter) => (value) => { setter(value); setPage(1); };

  const handleStudentSelect = (student) => {
    setStudentUuid(student.uuid);
    setStudentName(student.full_name);
    setPage(1);
  };

  const handleStudentClear = () => {
    setStudentUuid('');
    setStudentName('');
    setPage(1);
  };

  const buildParams = useCallback(() => {
    const params = { page, page_size: PAGE_SIZE };
    if (userEmail.trim()) params.user_email = userEmail.trim();
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (studentUuid) params.object_uuid = studentUuid;
    return params;
  }, [page, userEmail, dateFrom, dateTo, studentUuid]);

  const filters = buildParams();

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.auditLog.list(filters),
    queryFn: () => api.auditLog.getAll(filters),
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const entries    = data?.results ?? [];
  const totalCount = data?.count   ?? 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg mt-0.5">
          <Shield className="w-5 h-5 text-slate-600 dark:text-slate-300" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">{t('auditLog.title')}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {t('auditLog.description')}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
              {t('auditLog.filter.dateFrom')}
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => resetPage(setDateFrom)(e.target.value)}
              className="h-8 px-2 text-xs rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
              {t('auditLog.filter.dateTo')}
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={e => resetPage(setDateTo)(e.target.value)}
              className="h-8 px-2 text-xs rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <StudentFilter
            selectedName={studentName}
            onSelect={handleStudentSelect}
            onClear={handleStudentClear}
          />
          <Input
            className="w-52 h-8 text-sm"
            placeholder={t('auditLog.filter.userPlaceholder')}
            value={userEmail}
            onChange={e => resetPage(setUserEmail)(e.target.value)}
          />
          {totalCount > 0 && (
            <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
              {totalCount.toLocaleString()} {t('auditLog.entries')}
            </span>
          )}
      </div>

      {/* Table */}
      <div className="bg-card border rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="py-16 text-center text-sm text-gray-500 dark:text-gray-400">
            {t('auditLog.loading')}
          </div>
        ) : isError ? (
          <div className="py-16 text-center text-sm text-red-500">
            {t('auditLog.loadError')}
          </div>
        ) : entries.length === 0 ? (
          <div className="py-16 text-center">
            <Shield className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
            <p className="font-medium text-gray-700 dark:text-gray-300">{t('auditLog.noEntries')}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('auditLog.noEntriesDescription')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
                  <th className="px-4 py-3 font-medium whitespace-nowrap">{t('auditLog.col.timestamp')}</th>
                  <th className="px-4 py-3 font-medium">{t('auditLog.col.user')}</th>
                  <th className="px-4 py-3 font-medium">{t('auditLog.col.action')}</th>
                  <th className="px-4 py-3 font-medium">{t('auditLog.col.description')}</th>
                  <th className="px-4 py-3 font-medium">{t('auditLog.col.object')}</th>
                  <th className="px-4 py-3 font-medium">{t('auditLog.col.ip')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {entries.map(entry => (
                  <tr key={entry.uuid} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {formatTimestamp(entry.timestamp)}
                    </td>
                    <td className="px-4 py-3">
                      {entry.user ? (
                        <div>
                          <div className="text-sm font-medium">{entry.user.full_name || entry.user.email}</div>
                          <div className="text-xs text-gray-400">{entry.user.user_type}</div>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">{t('auditLog.system')}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <ActionBadge action={entry.action} display={entry.action_display} />
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <span
                        className="text-xs text-gray-600 dark:text-gray-300 line-clamp-2"
                        title={entry.description}
                      >
                        {entry.description}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {entry.model_name && (
                        <div>
                          <span className="font-medium">{entry.model_name}</span>
                          {entry.object_id && (
                            <span className="text-gray-400 ml-1">· {entry.object_id}</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400 font-mono whitespace-nowrap">
                      {entry.ip_address || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
          <span>{t('auditLog.pagination', { page, totalPages })}</span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
